import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import Layout from './Layout';
import MissionalRangeBar from '../components/MissionalRangeBar';
import { slugify, ensureUniqueSlug } from '../utils/slugify';

const MISSIONAL_SCALE_FALLBACK = {
  '-10': { phrase: 'No awareness of spiritual reality' },
  '-9': { phrase: 'There may be something' },
  '-8': { phrase: 'Some Awareness of God' },
  '-7': { phrase: "God is personal, but don't know what His Name is" },
  '-6': { phrase: 'Some Interest in Jesus Christ' },
  '-5': { phrase: 'Decided to investigate Jesus' },
  '-4': { phrase: 'Grasp the truth about Jesus' },
  '-3': { phrase: "Thinks Jesus is real. Don't know Him yet" },
  '-2': { phrase: 'Understands they have a sin problem' },
  '-1': { phrase: 'Acceptance of the Truth' },
  '0': { phrase: 'Brand New Believer' },
  '1': { phrase: 'Basic Faith in place' },
  '2': { phrase: 'Understands their identity in Christ' },
  '3': { phrase: 'Experiencing Change in their life' },
  '4': { phrase: 'Developing stronger faith' },
  '5': { phrase: 'Learning Christian Disciplines' },
  '6': { phrase: 'Assimilated into the Church' },
  '7': { phrase: 'Sharing their faith with others' },
  '8': { phrase: 'Growing in Leadership' },
  '9': { phrase: 'Living into their purpose in Christ' },
};

// Predefined categories that align with common discipleship topics
const CATEGORIES = [
  'Faith Foundations',
  'Life Questions',
  'Relationships',
  'Identity',
  'Purpose',
  'Suffering',
  'Marriage & Family',
  'Work & Calling',
  'Biblical Worldview',
  'Cultural Issues',
  'Apologetics',
  'Spiritual Growth',
  'Mental Health',
  'Prayer & Devotion',
  'Community & Church',
  'Mission & Evangelism'
];

const CollapsibleSection = ({ title, subtitle, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 pb-6 md:px-8 md:pb-8 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

const CreateContentResource = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [scaleInfoMap, setScaleInfoMap] = useState({});
  const [loadingDescriptions, setLoadingDescriptions] = useState(true);
  const [sortedLevels, setSortedLevels] = useState([]);
  const [trackName, setTrackName] = useState('');
  
  const [resource, setResource] = useState({
    title: '',
    description: '',
    type: 'video',
    url: '',
    articleContent: '', // For storing pasted article text
    author: '',
    authorWebsite: '',
    duration: '',
    tags: [],
    categories: [],
    transcription: '',
    keyTopics: [], // Key discussion points or questions addressed
    targetAudience: {
      maturityLevel: {
        blue: false,
        green: false,
        yellow: false
      },
      relationshipContext: [], // e.g., ['parent-child', 'mentor-mentee', 'marriage']
      lifeStage: [] // e.g., ['youth', 'young adult', 'parent', 'senior']
    },
    contentDetails: {
      platform: '', // e.g., YouTube, Spotify, etc.
      language: 'en',
      difficulty: 'beginner',
      format: '', // e.g., interview, sermon, discussion, etc.
      length: '', // e.g., 'short' (<10min), 'medium' (10-30min), 'long' (>30min)
    },
    missionalRange: {
      min: -10,
      max: 9  // Default to full range initially
    },
    relevantScaleLevels: [],
    articleSlug: '', // editable slug for article URL (auto-filled from title when empty)
  });

  const quillModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'header': [2, 3, false] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean'],
    ],
  }), []);

  useEffect(() => {
    const fetchDescriptions = async () => {
      setLoadingDescriptions(true);
      try {
        const scaleLevelsRef = collection(db, 'missionalScaleLevels');
        const querySnapshot = await getDocs(scaleLevelsRef);
        const descriptions = {};
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.level !== undefined) { 
            descriptions[data.level] = {
              phrase: data.phrase || 'No phrase defined', 
              description: data.description || 'No description defined'
            };
          }
        });
        if (Object.keys(descriptions).length > 0) {
          const levels = Object.keys(descriptions).map(Number).sort((a, b) => a - b);
          setSortedLevels(levels);
          setScaleInfoMap(descriptions);
        } else {
          throw new Error('Collection empty');
        }
      } catch (err) {
        console.warn("Using fallback missional scale data:", err.message || err);
        setScaleInfoMap(MISSIONAL_SCALE_FALLBACK);
        const levels = Object.keys(MISSIONAL_SCALE_FALLBACK).map(Number).sort((a, b) => a - b);
        setSortedLevels(levels);
      } finally {
        setLoadingDescriptions(false);
      }
    };

    fetchDescriptions();
  }, []);

  // Load track name
  useEffect(() => {
    const loadTrackName = async () => {
      if (!trackId) return;
      
      try {
        const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));
        
        if (trackDoc.exists()) {
          const trackData = trackDoc.data();
          setTrackName(trackData.name || 'Content Track');
        }
      } catch (error) {
        console.error('Error loading track name:', error);
      }
    };

    loadTrackName();
  }, [trackId]);

  const handleAddTag = (e) => {
    e.preventDefault();
    if (newTag.trim() && !resource.tags.includes(newTag.trim())) {
      setResource(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setResource(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleMissionalRangeChange = (type, value) => {
    setResource(prev => ({
      ...prev,
      missionalRange: {
        ...prev.missionalRange,
        [type]: parseInt(value),
        // If setting min, ensure max isn't lower
        ...(type === 'min' && parseInt(value) > prev.missionalRange.max 
          ? { max: parseInt(value) } 
          : {}),
        // If setting max, ensure min isn't higher
        ...(type === 'max' && parseInt(value) < prev.missionalRange.min 
          ? { min: parseInt(value) } 
          : {})
      }
    }));
  };

  const handleRelevantLevelChange = (level, isChecked) => {
    const numericLevel = Number(level);
    setResource(prev => ({
      ...prev,
      relevantScaleLevels: isChecked
        ? [...prev.relevantScaleLevels, numericLevel]
        : prev.relevantScaleLevels.filter(l => l !== numericLevel)
    }));
  };

  const handleSubmit = async (e, status = 'published') => {
    e.preventDefault();
    setLoading(true);

    try {
      let finalUrl = resource.url || '';
      let articleDocId = null;

      // Create the resource data object
      const resourceData = {
        title: resource.title || '',
        description: resource.description || '',
        type: resource.type || 'video',
        url: finalUrl,
        status: status,
        author: resource.author || '',
        authorWebsite: resource.authorWebsite || '',
        duration: resource.duration || '',
        tags: resource.tags || [],
        categories: resource.categories || [],
        transcription: resource.transcription || '',
        keyTopics: resource.keyTopics || [],
        targetAudience: {
          maturityLevel: resource.targetAudience?.maturityLevel || {
            blue: false,
            green: false,
            yellow: false
          },
          relationshipContext: resource.targetAudience?.relationshipContext || [],
          lifeStage: resource.targetAudience?.lifeStage || []
        },
        contentDetails: {
          platform: resource.contentDetails?.platform || '',
          language: resource.contentDetails?.language || 'en',
          difficulty: resource.contentDetails?.difficulty || 'beginner',
          format: resource.contentDetails?.format || '',
          length: resource.contentDetails?.length || ''
        },
        missionalRange: {
          min: resource.missionalRange.min,
          max: resource.missionalRange.max
        },
        relevantScaleLevels: resource.relevantScaleLevels || [],
        createdBy: {
          userId: currentUser?.uid || '',
          name: userData?.name || '',
          role: userData?.role || ''
        },
        dateCreated: serverTimestamp(),
        lastModified: serverTimestamp(),
        displayOrder: 999
      };

      // Add to the track's resources subcollection first (to get the resource ID)
      const resourceRef = await addDoc(
        collection(db, 'contentTracks', trackId, 'resources'),
        resourceData
      );

      // If it's an article, create the public-facing article document with all metadata
      const hasArticleContent = resource.articleContent && resource.articleContent.replace(/<[^>]*>/g, '').trim().length > 0;
      if (resource.type === 'article' && hasArticleContent) {
        const baseSlug = (resource.articleSlug && slugify(resource.articleSlug)) || slugify(resource.title);
        const slug = baseSlug ? await ensureUniqueSlug(baseSlug) : null;

        const articleRef = await addDoc(collection(db, 'articles'), {
          title: resource.title,
          slug: slug,
          content: resource.articleContent,
          description: resource.description || '',
          author: resource.author || '',
          authorName: resource.author || '',
          authorWebsite: resource.authorWebsite || '',
          website: resource.authorWebsite || '',
          category: (resource.categories && resource.categories[0]) || '',
          trackId: trackId,
          resourceId: resourceRef.id,
          createdAt: serverTimestamp(),
          createdBy: {
            userId: currentUser?.uid || '',
            name: userData?.name || '',
          },
        });

        const articleDomain = process.env.REACT_APP_ARTICLE_DOMAIN || 'https://tmrsrc.com';
        finalUrl = `${articleDomain}/article/${slug || articleRef.id}`;
        articleDocId = articleRef.id;

        await updateDoc(
          doc(db, 'contentTracks', trackId, 'resources', resourceRef.id),
          { url: finalUrl, articleDocId: articleRef.id }
        );
      }

      console.log('Resource created successfully:', resourceRef.id);
      navigate(`/content-tracks/${trackId}`);
    } catch (error) {
      console.error('Error creating resource:', error);
      alert('Failed to create resource. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 md:p-8">
        {/* Type Selector - At the top */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
            Resource Type
          </label>
          <select
            value={resource.type}
            onChange={(e) => setResource(prev => ({
              ...prev,
              type: e.target.value
            }))}
            className="w-full max-w-xs p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] bg-white font-medium text-gray-900 shadow-sm hover:border-gray-300 transition-colors"
          >
            <option value="video">Video</option>
            <option value="article">Article</option>
            <option value="podcast">Podcast</option>
            <option value="book">Book</option>
            <option value="course">Course</option>
          </select>
        </div>

        {/* Track Info and Title */}
        {trackName && (
          <div className="mb-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Content Track</p>
            <h2 className="text-2xl font-bold text-gray-900 mt-1">{trackName}</h2>
          </div>
        )}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
          {trackName ? `Add Resource to ${trackName}` : 'Add Resource to Track'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 pb-3 border-b border-gray-200">Basic Information</h2>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Resource Title
                </label>
                <input
                  type="text"
                  value={resource.title}
                  onChange={(e) => {
                    const newTitle = e.target.value;
                    setResource(prev => ({
                      ...prev,
                      title: newTitle,
                      articleSlug: prev.articleSlug === '' ? slugify(newTitle) : prev.articleSlug
                    }));
                  }}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                  required
                  placeholder="e.g., 'Tim Keller - Why Does God Allow Suffering?'"
                />
              </div>

              {/* Article URL slug - only for articles */}
              {resource.type === 'article' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    URL slug
                    <span className="text-gray-500 text-xs ml-2 font-normal">
                      (Used in the article URL. Editable to avoid duplicates or customize.)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={resource.articleSlug}
                    onChange={(e) => setResource(prev => ({ ...prev, articleSlug: e.target.value }))}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors font-mono text-sm"
                    placeholder={slugify(resource.title) || 'e.g. my-article-title'}
                  />
                  {resource.articleSlug && (
                    <p className="text-xs text-gray-500 mt-1">
                      Preview: {process.env.REACT_APP_ARTICLE_DOMAIN || 'https://tmrsrc.com'}/article/{resource.articleSlug.replace(/[^a-z0-9-]/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || slugify(resource.title)}
                    </p>
                  )}
                </div>
              )}

              {/* URL or Article Content - conditional based on type */}
              {resource.type === 'article' ? (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Article Content
                    <span className="text-gray-500 text-xs ml-2 font-normal">
                      (Write or paste your article. A URL will be generated automatically.)
                    </span>
                  </label>
                  <div className="border-2 border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#0076BE] focus-within:border-[#0076BE] transition-colors">
                    <ReactQuill
                      theme="snow"
                      value={resource.articleContent}
                      onChange={(value) => setResource(prev => ({
                        ...prev,
                        articleContent: value
                      }))}
                      modules={quillModules}
                      placeholder="Write your article content here..."
                      style={{ minHeight: '250px' }}
                    />
                  </div>
                  {resource.articleContent && resource.articleContent !== '<p><br></p>' && (
                    <p className="text-xs text-gray-500 mt-2 font-medium">
                      {resource.articleContent.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(word => word.length > 0).length} words
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Resource URL
                  </label>
                  <input
                    type="url"
                    value={resource.url}
                    onChange={(e) => setResource(prev => ({
                      ...prev,
                      url: e.target.value
                    }))}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                    required={resource.type !== 'article'}
                    placeholder="https://..."
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={resource.description}
                  onChange={(e) => setResource(prev => ({
                    ...prev,
                    description: e.target.value
                  }))}
                  rows={4}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                  required
                  placeholder="Describe what this resource is about and why it's helpful..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Author
                  </label>
                  <input
                    type="text"
                    value={resource.author}
                    onChange={(e) => setResource(prev => ({
                      ...prev,
                      author: e.target.value
                    }))}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                    placeholder="Author name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Author Website
                  </label>
                  <input
                    type="url"
                    value={resource.authorWebsite}
                    onChange={(e) => setResource(prev => ({
                      ...prev,
                      authorWebsite: e.target.value
                    }))}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Duration
                </label>
                <input
                  type="text"
                  value={resource.duration}
                  onChange={(e) => setResource(prev => ({
                    ...prev,
                    duration: e.target.value
                  }))}
                  className="w-full max-w-xs p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                  placeholder="e.g., '5:30' or '10 min read'"
                />
              </div>

              {/* Categories */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Categories
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {CATEGORIES.map(category => (
                    <label key={category} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={resource.categories.includes(category)}
                        onChange={(e) => {
                          setResource(prev => ({
                            ...prev,
                            categories: e.target.checked
                              ? [...prev.categories, category]
                              : prev.categories.filter(c => c !== category)
                          }));
                        }}
                        className="w-4 h-4 rounded border-2 border-gray-300 text-[#0076BE] focus:ring-2 focus:ring-[#0076BE] focus:ring-offset-0"
                      />
                      <span className="text-sm text-gray-700">{category}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {resource.tags.map(tag => (
                    <span
                      key={tag}
                      className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1.5 rounded-full flex items-center gap-2 shadow-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-blue-600 hover:text-blue-800 font-bold text-lg leading-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    className="flex-1 p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                    placeholder="Add a tag..."
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-6 py-3 bg-[#0076BE] text-white font-semibold rounded-lg hover:bg-[#0065a3] transition-colors shadow-sm"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Transcription - only show for video/audio/podcast */}
              {(resource.type === 'video' || resource.type === 'podcast') && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Transcription
                    <span className="text-gray-500 text-xs ml-2 font-normal">
                      (Add video/audio transcription for better searchability)
                    </span>
                  </label>
                  <textarea
                    value={resource.transcription}
                    onChange={(e) => setResource(prev => ({
                      ...prev,
                      transcription: e.target.value
                    }))}
                    rows={6}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors font-mono text-sm"
                    placeholder="Paste or type transcription here..."
                  />
                </div>
              )}

              {/* Key Topics */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Key Topics/Questions Addressed
                </label>
                <textarea
                  value={resource.keyTopics.join('\n')}
                  onChange={(e) => setResource(prev => ({
                    ...prev,
                    keyTopics: e.target.value.split('\n').filter(topic => topic.trim())
                  }))}
                  rows={4}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                  placeholder="Enter key topics or questions (one per line)..."
                />
              </div>

            </div>
          </div>

          {/* Target Audience - Collapsible */}
          <CollapsibleSection title="Target Audience" subtitle="Optional - Maturity level, relationship context, and life stage">
            <div className="space-y-5 pt-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Maturity Level
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'blue', label: 'Blue' },
                    { key: 'green', label: 'Green' },
                    { key: 'yellow', label: 'Yellow' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center space-x-3 p-3 rounded-lg bg-white border-2 border-gray-200 hover:border-gray-300 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={resource.targetAudience.maturityLevel[key]}
                        onChange={(e) => setResource(prev => ({
                          ...prev,
                          targetAudience: {
                            ...prev.targetAudience,
                            maturityLevel: { ...prev.targetAudience.maturityLevel, [key]: e.target.checked }
                          }
                        }))}
                        className="w-4 h-4 rounded border-2 border-gray-300 text-[#0076BE] focus:ring-2 focus:ring-[#0076BE] focus:ring-offset-0"
                      />
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Relationship Context
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {['parent-child', 'mentor-mentee', 'marriage'].map(ctx => (
                    <label key={ctx} className="flex items-center space-x-3 p-3 rounded-lg bg-white border-2 border-gray-200 hover:border-gray-300 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={resource.targetAudience.relationshipContext.includes(ctx)}
                        onChange={(e) => {
                          setResource(prev => ({
                            ...prev,
                            targetAudience: {
                              ...prev.targetAudience,
                              relationshipContext: e.target.checked
                                ? [...prev.targetAudience.relationshipContext, ctx]
                                : prev.targetAudience.relationshipContext.filter(c => c !== ctx)
                            }
                          }));
                        }}
                        className="w-4 h-4 rounded border-2 border-gray-300 text-[#0076BE] focus:ring-2 focus:ring-[#0076BE] focus:ring-offset-0"
                      />
                      <span className="text-sm font-medium text-gray-700 capitalize">{ctx.replace('-', ' → ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Life Stage
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {['youth', 'young adult', 'parent', 'senior'].map(stage => (
                    <label key={stage} className="flex items-center space-x-3 p-3 rounded-lg bg-white border-2 border-gray-200 hover:border-gray-300 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={resource.targetAudience.lifeStage.includes(stage)}
                        onChange={(e) => {
                          setResource(prev => ({
                            ...prev,
                            targetAudience: {
                              ...prev.targetAudience,
                              lifeStage: e.target.checked
                                ? [...prev.targetAudience.lifeStage, stage]
                                : prev.targetAudience.lifeStage.filter(c => c !== stage)
                            }
                          }));
                        }}
                        className="w-4 h-4 rounded border-2 border-gray-300 text-[#0076BE] focus:ring-2 focus:ring-[#0076BE] focus:ring-offset-0"
                      />
                      <span className="text-sm font-medium text-gray-700 capitalize">{stage}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* Content Details - Collapsible */}
          <CollapsibleSection title="Content Details" subtitle="Optional - Platform, language, difficulty, format, and length">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Platform</label>
                <input
                  type="text"
                  value={resource.contentDetails.platform}
                  onChange={(e) => setResource(prev => ({
                    ...prev,
                    contentDetails: { ...prev.contentDetails, platform: e.target.value }
                  }))}
                  className="w-full p-3 bg-white border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                  placeholder="e.g., YouTube, Spotify, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Language</label>
                <input
                  type="text"
                  value={resource.contentDetails.language}
                  onChange={(e) => setResource(prev => ({
                    ...prev,
                    contentDetails: { ...prev.contentDetails, language: e.target.value }
                  }))}
                  className="w-full p-3 bg-white border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                  placeholder="e.g., English"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Difficulty</label>
                <select
                  value={resource.contentDetails.difficulty}
                  onChange={(e) => setResource(prev => ({
                    ...prev,
                    contentDetails: { ...prev.contentDetails, difficulty: e.target.value }
                  }))}
                  className="w-full p-3 bg-white border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Format</label>
                <input
                  type="text"
                  value={resource.contentDetails.format}
                  onChange={(e) => setResource(prev => ({
                    ...prev,
                    contentDetails: { ...prev.contentDetails, format: e.target.value }
                  }))}
                  className="w-full p-3 bg-white border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                  placeholder="e.g., interview, sermon, discussion, etc."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Length</label>
                <input
                  type="text"
                  value={resource.contentDetails.length}
                  onChange={(e) => setResource(prev => ({
                    ...prev,
                    contentDetails: { ...prev.contentDetails, length: e.target.value }
                  }))}
                  className="w-full p-3 bg-white border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                  placeholder="e.g., 'short' (<10min), 'medium' (10-30min), 'long' (>30min)"
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* Missional Scale - Collapsible, combined range + levels */}
          <CollapsibleSection title="Missional Scale" subtitle="Optional - Select the scale range and specific levels this resource targets">
            <div className={`space-y-8 pt-4 ${loadingDescriptions ? 'opacity-50' : ''}`}>
              {/* Range */}
              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-3">Scale Range</h3>
                <div className="mb-4">
                  <MissionalRangeBar 
                    min={resource.missionalRange.min} 
                    max={resource.missionalRange.max} 
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Min Level</label>
                    <select
                      value={resource.missionalRange.min}
                      onChange={(e) => handleMissionalRangeChange('min', e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                      disabled={loadingDescriptions}
                    >
                      {loadingDescriptions ? (
                        <option>Loading...</option>
                      ) : (
                        Object.entries(scaleInfoMap).map(([level, info]) => (
                          <option key={level} value={level}>
                            {level}: {info.phrase}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Max Level</label>
                    <select
                      value={resource.missionalRange.max}
                      onChange={(e) => handleMissionalRangeChange('max', e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                      disabled={loadingDescriptions}
                    >
                      {loadingDescriptions ? (
                        <option>Loading...</option>
                      ) : (
                        Object.entries(scaleInfoMap).map(([level, info]) => (
                          parseInt(level) >= resource.missionalRange.min && (
                            <option key={level} value={level}>
                              {level}: {info.phrase}
                            </option>
                          )
                        ))
                      )}
                    </select>
                  </div>
                </div>
              </div>

              {/* Relevant Levels */}
              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-3">Relevant Levels</h3>
                <p className="text-sm text-gray-500 mb-4">Select the specific levels this resource is most relevant for.</p>
                {loadingDescriptions ? (
                  <p className="text-gray-500 font-medium">Loading levels...</p>
                ) : sortedLevels.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {sortedLevels.map((level) => (
                      <label key={level} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={resource.relevantScaleLevels.includes(level)}
                          onChange={(e) => handleRelevantLevelChange(level, e.target.checked)}
                          className="w-4 h-4 rounded border-2 border-gray-300 text-[#0076BE] focus:ring-2 focus:ring-[#0076BE] focus:ring-offset-0"
                          disabled={loadingDescriptions}
                        />
                        <span className="text-sm text-gray-700">
                          {level}: {scaleInfoMap[level]?.phrase || '...'}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-amber-600 font-medium text-sm">Scale levels not yet seeded. Visit App Admin to seed them.</p>
                )}
              </div>
            </div>
          </CollapsibleSection>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={(e) => handleSubmit(e, 'draft')}
              disabled={loading}
              className="px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg border-2 border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Saving...' : 'Save as Draft'}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-[#0076BE] text-white font-semibold rounded-lg hover:bg-[#0065a3] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0076BE] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
            >
              {loading ? 'Publishing...' : 'Publish Resource'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateContentResource; 